import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, Plus, Star, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  useCreateGtin,
  useDeleteGtin,
  useRegistrationGtins,
  useSetPrimaryGtin,
} from './item-registration.api';

/**
 * The barcodes one registration item answers to.
 *
 * An item usually has several — a case, a single, a relabelled run — and
 * exactly one of them is primary: the one the item shows as its own. That is
 * why setting primary is a separate act from adding.
 */
export function GtinsSheet({
  itemId,
  title,
  open,
  onOpenChange,
}: {
  itemId: string | null;
  title: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();

  const gtins = useRegistrationGtins(open ? itemId : null);
  const create = useCreateGtin(itemId);
  const remove = useDeleteGtin(itemId);
  const setPrimary = useSetPrimaryGtin(itemId);

  const [draft, setDraft] = useState('');

  const rows = gtins.data ?? [];
  const duplicate = rows.some((row) => row.gtin.trim() === draft.trim());
  const canAdd = draft.trim().length > 0 && !duplicate;

  const failed = () => toast.error(t('itemRegistration.actionFailed'));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-md"
      >
        <SheetHeader className="border-b pr-12">
          <SheetTitle>{title}</SheetTitle>
        </SheetHeader>

        <div className="space-y-3 p-4">
          <form
            className="flex gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (!canAdd) return;
              create.mutate(draft, {
                onSuccess: () => setDraft(''),
                onError: failed,
              });
            }}
          >
            <Input
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder={t('itemRegistration.newGtin')}
              aria-label={t('itemRegistration.newGtin')}
              className="font-mono"
            />
            <Button type="submit" disabled={!canAdd || create.isPending}>
              {create.isPending ? (
                <Loader2 className="animate-spin" />
              ) : (
                <Plus />
              )}
              {t('itemRegistration.add')}
            </Button>
          </form>

          {/* Said before the button is pressed, not after it fails. */}
          {duplicate && (
            <p className="text-muted-foreground text-xs">
              {t('itemRegistration.gtinAlreadyListed')}
            </p>
          )}

          {gtins.isPending && (
            <div className="divide-y rounded-xl border">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="flex items-center gap-2 p-3">
                  <Skeleton className="h-4 flex-1" />
                  <Skeleton className="h-6 w-16" />
                </div>
              ))}
            </div>
          )}

          {gtins.isSuccess && rows.length === 0 && (
            <p className="text-muted-foreground rounded-xl border border-dashed py-10 text-center text-sm">
              {t('itemRegistration.noGtins')}
            </p>
          )}

          {rows.length > 0 && (
            <div className="relative rounded-xl">
              <BusyOverlay show={gtins.isFetching} />
              <div
                className={cn(
                  'divide-y rounded-xl border',
                  gtins.isFetching && 'opacity-60 transition-opacity'
                )}
              >
                {rows.map((row) => (
                  <div key={row.id} className="flex items-center gap-2 p-3">
                    <span className="min-w-0 flex-1 font-mono text-sm break-all">
                      {row.gtin}
                    </span>

                    {row.isPrimary ? (
                      <Badge variant="success">
                        {t('itemRegistration.primary')}
                      </Badge>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={setPrimary.isPending}
                        onClick={() =>
                          setPrimary.mutate(row.id, { onError: failed })
                        }
                      >
                        <Star className="size-3.5" />
                        {t('itemRegistration.makePrimary')}
                      </Button>
                    )}

                    <Button
                      variant="ghost"
                      size="icon-sm"
                      className="text-destructive"
                      aria-label={t('itemRegistration.remove')}
                      // The primary barcode is the item's identity; removing it
                      // would leave the row with none.
                      disabled={row.isPrimary || remove.isPending}
                      onClick={() => remove.mutate(row.id, { onError: failed })}
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
